import {
  ApiClientError,
  Content,
  CopyFolder,
  CopyPlaylist,
  CopyPresentation,
} from '@raydiant/api-client-js';
import { useQueryClient } from 'react-query';
import raydiant from '../clients/raydiant';
import { keys } from '../queryClient';
import useProtectedMutation from './useProtectedMutation';

type CopyResult = CopyPresentation | CopyPlaylist | CopyFolder;

type CopyParams = {
  name: string;
  // For playlists/presentations: the destination is reached via a follow-up
  // move call. For folders: it's sent as `target_folder_id` on the same copy
  // request — no separate move needed.
  folderId?: string;
  // How to handle content owned by other users that lives outside the copied
  // tree. Left `undefined` on the first ("preflight") request: when the server
  // detects such content it answers with `hasOutOfTreeUnowned: true` and copies
  // nothing, prompting the user to choose. The chosen boolean is then sent on a
  // second request to actually perform the copy (true = copy the unowned files,
  // false = link to the originals).
  copyOutOfTreeUnowned?: boolean;
};

export default function useCopyContent() {
  const queryClient = useQueryClient();

  const mutation = useProtectedMutation<
    CopyResult,
    ApiClientError,
    { content: Content; params: CopyParams }
  >(
    async ({ content, params }) => {
      const { name, folderId, copyOutOfTreeUnowned } = params;

      // A preflight is the first request (no choice made yet) that comes back
      // flagging unowned content as `{ hasOutOfTreeUnowned: true }`: the server
      // copied nothing, so we skip the follow-up move and surface it so the
      // caller can open the modal. (`'hasOutOfTreeUnowned' in result` rather than
      // `result.hasOutOfTreeUnowned` because the performed-copy branch of the
      // union has no such property; the guard return type narrows `copy` to the
      // resource below.)
      const isPreflightOnly = (
        result: CopyResult,
      ): result is Extract<CopyResult, { hasOutOfTreeUnowned: true }> =>
        copyOutOfTreeUnowned === undefined && 'hasOutOfTreeUnowned' in result;

      switch (content.resourceType) {
        case 'presentation': {
          const copy = await raydiant.copyPresentation(content.id, {
            name,
            copyOutOfTreeUnowned,
          });
          if (isPreflightOnly(copy)) return copy;
          if (folderId) {
            await raydiant.movePresentationToFolder(copy.id, folderId);
          }
          return copy;
        }
        case 'playlist': {
          const copy = await raydiant.copyPlaylist(content.id, {
            name,
            copyOutOfTreeUnowned,
          });
          if (isPreflightOnly(copy)) return copy;
          if (folderId) {
            await raydiant.movePlaylistToFolder(copy.id, folderId);
          }
          return copy;
        }
        case 'folder': {
          return await raydiant.copyFolder(content.id, {
            name,
            targetFolderId: folderId,
            copyOutOfTreeUnowned,
          });
        }
        default:
          throw new Error(`Unknown resource type: ${content.resourceType}`);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(keys.library());
      },
    },
  );

  return mutation;
}
